@use '../../scss/utility/functions' as utility;
@use './round-button-utility.scss' as innerUtility;
@use './timer-animation/timer-animation';

:host {
  --border-color: var(--wb-transparent);
  --highlight-color: var(--wb-blue-45);
  --background-color: var(--wb-transparent);
  --box-shadow: none;

  @include innerUtility.generate-states-colors-vars();

  display: inline-block;
  position: relative;

  .button {
    -webkit-tap-highlight-color: var(--wb-transparent);
    align-items: center;
    appearance: none;
    background-color: var(--background-color);
    border-width: utility.wb-pixel-to-rem(1px);
    border-style: solid;
    border-color: var(--border-color);
    border-radius: 100%;
    cursor: pointer;
    display: inline-flex;
    font-size: 0;
    justify-content: center;
    line-height: 0;
    outline: none;
    padding: 0;
    text-decoration: none;
    box-sizing: border-box;
    box-shadow: var(--box-shadow);

    &::-moz-focus-inner {
      border-style: none;
      padding: 0;
    }

    &:not([disabled]) {
      @include innerUtility.generate-states-colors(('hover', 'focus', 'active'));
    }
  }
}

::slotted(.wb-icon) {
  margin-inline-end: 0;
  color: var(--wb-grey-20);
  pointer-events: none; // why is this needed?
  z-index: 4;
}

:host([disabled]) .button {
  opacity: 0.4;
  cursor: not-allowed;
}

:host([size='s']) {
  .button {
    width: utility.wb-pixel-to-rem(36px);
    height: utility.wb-pixel-to-rem(36px);
  }

  & ::slotted(.wb-icon) {
    --size: #{utility.wb-pixel-to-rem(12px)};
  }

  & ::slotted(.wb-icon[name*='/']) {
    --size: #{utility.wb-pixel-to-rem(24px)};
  }
}

:host([size='m']) {
  .button {
    width: utility.wb-pixel-to-rem(48px);
    height: utility.wb-pixel-to-rem(48px);
  }

  & ::slotted(.wb-icon) {
    --size: #{utility.wb-pixel-to-rem(16px)};
  }

  & ::slotted(.wb-icon[name*='/']) {
    --size: #{utility.wb-pixel-to-rem(24px)};
  }
}

:host([size='l']) {
  .button {
    width: utility.wb-pixel-to-rem(64px);
    height: utility.wb-pixel-to-rem(64px);
  }

  & ::slotted(.wb-icon) {
    --size: #{utility.wb-pixel-to-rem(24px)};
  }

  & ::slotted(.wb-icon[name*='/']) {
    --size: #{utility.wb-pixel-to-rem(32px)};
  }
}

:host([elevation='xs']) {
  --border-color: var(--wb-grey-85);
}

:host([elevation='s']) {
  --box-shadow: var(--wb-shadow-s);
}

:host([elevation='m']) {
  ---box-shadow: var(--wb-shadow-m);
}

:host([elevation='l']) {
  --box-shadow: var(--wb-shadow-l);
}

:host([elevation]) {
  --background-color: var(--wb-white);

  @include innerUtility.generate-states-colors-vars(
    (
      'hover': (
        --wb-grey-85,
        --wb-grey-70,
      ),
      'focus': (
        --wb-white,
        --highlight-color,
      ),
      'active': (
        --wb-grey-85,
        --highlight-color,
      ),
    )
  );

  .button:not([disabled]) {
    @include innerUtility.generate-states-colors(('hover', 'focus', 'active'));
  }
}

:host([theme='dark']) {
  --highlight-color: var(--wb-blue-50);

  @include innerUtility.generate-states-colors-vars(
    (
      'hover': (
        --wb-grey-15,
        --wb-grey-15,
      ),
      'focus': (
        --wb-grey-15,
        --highlight-color,
      ),
      'active': (
        --wb-grey-40,
        --highlight-color,
      ),
    )
  );

  .button {
    color: var(--wb-white);

    &:not([disabled]) {
      @include innerUtility.generate-states-colors(('hover', 'focus', 'active'));
    }
  }
}

:host([theme='dark']) ::slotted(.wb-icon) {
  color: inherit;
}

:host([theme='dark'][elevation]),
:host([theme='dark'][duration]) {
  --background-color: var(--wb-black);
  --border-color: var(--wb-grey-20);

  @include innerUtility.generate-states-colors-vars(
    (
      'hover': (
        --wb-grey-15,
        --wb-grey-40,
      ),
      'active': (
        --wb-grey-40,
        --highlight-color,
      ),
    )
  );

  .button {
    &:not([disabled]):hover {
      @include innerUtility.generate-states-colors(('hover'));
    }

    &:not([disabled]):active {
      @include innerUtility.generate-states-colors(('active'));
    }
  }
}

:host([theme='dark'][elevation='s']) .button {
  --background-color: var(--wb-grey-15);
}
